import React, { useState, Suspense, useEffect, useContext } from 'react';
import './styles/main.scss';
import { ApolloProvider } from '@apollo/client';
import { BrowserRouter, Route, Switch, Redirect } from 'react-router-dom';
import { CompatRouter } from 'react-router-dom-v5-compat';
import { logout } from '@lib/api';
import client from '@lib/graphql';

import { LayoutBase, LayoutSimple, LayoutSideMenu, LayoutNoHeader } from './layouts';
import {
	HomePage,
	Administration,
	Clients,
	NotFoundPage,
	Projects,
	Project,
	Setup,
	Tenders,
	Opportunity,
	Reports,
	Icone,
	Profile,
	DownloadFromLink,
	Dashboard,
} from '@pages';

import { Spinner } from 'react-bootstrap';
import { GlobalToastList } from '@components/GlobalToastList';
import { LevelContext } from '@components/contexts/LevelContext';
import { UserProvider, UserContext } from '@components/contexts/UserProvider';
import { PermissionsProvider } from '@components/contexts/PermissionsProvider';
import ScopedCssBaseline from '@mui/material/ScopedCssBaseline';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { OETrans } from 'components/translation/OETrans';
import { LoginSSO } from 'pages';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

const theme = createTheme({
	typography: {
		fontFamily: 'Barlow',
	},
	components: {
		MuiCssBaseline: {
			styleOverrides: {
				'@font-face': {
					fontFamily: 'Barlow',
					//src: `url(${InspirationTTF}) format("truetype")`
				},
				/* body: {
			  fontSize: "3rem",
			  color: "purple"
			} */
			},
		},
	},
});

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnMount: false,
			refetchOnWindowFocus: false,
		},
	},
});

const App = () => {
	const { setEmailForPasswordReset, emailForPasswordReset } = useContext(LevelContext);
	const [userLanguage, setUserLanguage] = useState(localStorage.getItem('userLanguage') ?? 'IT');
	const [translations, setTranslations] = useState(JSON.parse(localStorage.getItem('translations')) ?? []);
	//const [userRole, setUserRole] = useState(Number(localStorage.getItem('userRole')) ?? 3); // [1,2,3
	const [apiError, setApiError] = useState(false);
	const [devMode, setDevMode] = useState(localStorage.getItem('devMode') === 'true' ? true : false);

	useEffect(() => {
		let translations = localStorage.getItem('translations');
		translations = translations ? JSON.parse(translations) : [];

		setTranslations(translations);
	}, []);

	useEffect(() => {
		localStorage.setItem('devMode', devMode);
	}, [devMode]); // eslint-disable-line react-hooks/exhaustive-deps

	window.addEventListener('storage', function (e) {
		// Check if the userRole has changed and not exists impersonate key
		if (e.storageArea === localStorage && e.key === 'token' && !localStorage.getItem('impersonate')) {
			if (e.oldValue !== e.newValue && e.oldValue !== null) {
				// Go to logout page
				window.location.href = '/logout';
			}
		}
	});

	return (
		<>
			{apiError ? (
				<div>
					<OETrans code="common.error.backend" fallback={'Errore di connessione col backend'} />
				</div>
			) : (
				<Suspense fallback="loading">
					<QueryClientProvider client={queryClient}>
						<ApolloProvider client={client}>
							<BrowserRouter>
								<CompatRouter>
									{' '}
									{/* for react-router-dom v6 compatibility with v5 */}
									<LevelContext.Provider
										value={{
											userLanguage,
											setUserLanguage,
											translations,
											setTranslations,
											devMode,
											setDevMode,
											setEmailForPasswordReset,
											emailForPasswordReset,
										}}
									>
										{/* value is mandatory for Context.Provider */}

										<UserProvider>
											<PermissionsProvider>
												<ThemeProvider theme={theme}>
													{' '}
													{/* for add custom style to Material components */}
													<ScopedCssBaseline /> {/* for add custom style to Material components only to the children */}
													<Switch>
														<UnauthenticatedRoute
															path="/"
															exact
															component={(props) => <HomePage setApiError={setApiError} />}
															layout={LayoutNoHeader} /* {LayoutSimple} */
														/>
														<UnauthenticatedRoute path="/kc" component={(props) => <LoginSSO />} layout={LayoutNoHeader} />

														{/* path="/download?file=:fileId&expires=:expiresData&signature=:signature" */}
														<UnauthenticatedRoute path="/download" component={() => <DownloadFromLink />} layout={LayoutBase} />

														<UnauthenticatedRoute path="/icone" exact component={() => <Icone />} layout={LayoutSimple} />

														<LogoutRoute path="/logout" exact />

														{/* props contiene match,location,history mi serve per capire la rotta esatta e caricare il giusto sottocomponente */}
														<AuthenticatedRoute
															path="/projects"
															exact
															component={() => <Projects />}
															layout={LayoutSimple}
															roles={[1, 2, 3]}
															//permissions={['view-projects-list']} //dubbio se sia meglio usare le permissions qui o nei componenti Page
														/>

														<AuthenticatedRoute
															path="/project/:projectId/:tab?/:submenu?/:subtab?" // /execution/economic/contracts
															component={(props) => <Project {...props} />}
															layout={LayoutBase}
															roles={[1, 2, 3, 4]}
														/>

														<AuthenticatedRoute
															path="/administration"
															exact
															component={(props) => <Administration {...props} />}
															layout={LayoutBase}
															//roles={[2, 3]}
														/>

														<AuthenticatedRoute
															path="/clients" //?groups
															exact
															component={(props) => <Clients {...props} />}
															layout={LayoutBase}
															//roles={[2, 3]}
														/>

														<AuthenticatedRoute
															path="/setup"
															component={() => <Setup />}
															layout={LayoutBase}
															//roles={[2, 3]}
														/>

														<AuthenticatedRoute
															path="/tenders"
															component={() => <Tenders />}
															layout={LayoutSideMenu}
															//roles={[2, 3]}
														/>

														<AuthenticatedRoute path="/opportunity" exact component={() => <Opportunity />} layout={LayoutBase} />

														<AuthenticatedRoute
															path="/reports"
															exact
															component={() => <Reports />}
															layout={LayoutSimple}
															roles={[1, 2, 3]}
														/>

														<AuthenticatedRoute path="/profile" exact component={() => <Profile />} layout={LayoutSideMenu} />
														<AuthenticatedRoute path="/dashboard" exact component={() => <Dashboard />} layout={LayoutSideMenu} />

														<AuthenticatedRoute component={NotFoundPage} layout={LayoutBase} />

														<UnauthenticatedRoute component={NotFoundPage} layout={LayoutBase} />
													</Switch>
												</ThemeProvider>
											</PermissionsProvider>
										</UserProvider>
									</LevelContext.Provider>
								</CompatRouter>
							</BrowserRouter>
						</ApolloProvider>
					</QueryClientProvider>
				</Suspense>
			)}
		</>
	);
};

const AuthenticatedRoute = ({ component: Component, layout: Layout, ...rest }) => {
	const [hasRole /* setHasRole */] = useState(true);

	if (hasRole /* && rest.roles.includes(userRole) */) {
		return (
			<Route
				{...rest}
				render={(props) => {
					return (
						<Layout {...props}>
							<Component {...props} />
							<GlobalToastList />
						</Layout>
					);
				}}
			/>
		);
	} else {
		return (
			<div className="App">
				<div className="d-flex aligns-items-center justify-content-center align-items-center" style={{ height: '100vh' }}>
					<Spinner animation="border" role="status">
						<span style={{ visibility: 'hidden' }}>
							<OETrans code="common.loading" fallback={'Caricamento'} />
							...
						</span>
					</Spinner>
				</div>
			</div>
		);
	}
};

const UnauthenticatedRoute = ({ component: Component, layout: Layout, ...rest }) => {
	if (rest.auth === true && rest.location.pathname === '/') {
		return <Redirect to={{ pathname: '/projects', state: { referrer: rest.location.pathname } }} />;
	} else {
		return (
			<Route
				{...rest}
				render={(props) => (
					<Layout {...props}>
						<Component {...props} />
						<GlobalToastList />
					</Layout>
				)}
			/>
		);
	}
};

const LogoutRoute = ({ component: Component, layout: Layout, ...rest }) => {
	const { logoutContext } = useContext(UserContext);

	useEffect(() => {
		logoutContext();
	}, [logoutContext]);

	logout();

	return <Redirect to="/" />;
};

export default App;
